import React, { useState, useRef, useMemo, useEffect } from "react";
import SvgModal from "./components/SvgModal";
import TractumSvgEditor from "./components/TractumSvg"
import { createPortal } from "react-dom";


export default function PageDatasetTable({ pageDataset, setPageDataset, pageId,parentData }) {
  const columns = pageDataset?.columns || [];  
  const { project,section,group,page,folder } = parentData;  
  const samplePoints = [
    { id: "point-1", x: 150, y: 150, r: 15, color: "red", status: "pending", owner: "Alice" },
    { id: "point-2", x: 400, y: 300, r: 15, color: "green", status: "done", owner: "Bob" },
    { id: "point-3", x: 600, y: 200, r: 15, color: "blue", status: "in progress", owner: "Charlie" },
  ];
  const tractumPoints = [
  {
    id: "p01",
    position: { x: 150, y: 150 },
    dimension: { r: 15 },

    semantic: {
      type: "entity",              // or task, sensor, node, person, issue, etc.
      label: "Air Quality Sensor",
      status: "pending",
      owner: "Alice",
      tags: ["sensor", "iaq", "monitor"],
      linkedDataId: "SENSOR-001",   // pointer to Django container model
    },

    relations: {
      connectsTo: ["p02"],
      dependsOn: [],
      children: [],
    },

    behavior: {
      onClick: "openPanel:iaq-sensor-panel",
      onHover: "showTooltip",
      onUpdate: "syncWithModel",
    },

    style: {
      color: "red",
      opacity: 0.9,
      pulse: true,
    }
  }
];

  const [points, setPoints] = useState(samplePoints);


  const handlePointClick = (id) => {
    alert(`Clicked on ${id} — you can load parameters from DB here`);
  };
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState(Object.fromEntries(columns.map(c => [c, ""])));
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editRow, setEditRow] = useState(null); // row being edited
  const [searchTerm, setSearchTerm] = useState("");
  const [popupSearch, setPopupSearch] = useState("");
  const containerRef = useRef(null);

  const [modal, setModal] = useState({ type: null, row: null });

  // Example for opening edit modal
  const openEditModal = (row) => setModal({ type: "edit", row });

  const [checkboxFilters, setCheckboxFilters] = useState({});
  const [activeFilterPopup, setActiveFilterPopup] = useState(null); 
  const headerRefs = useRef({});
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const openChecklistModal = (row) => setModal({ type: "checklist", row });
  const openSVGModal = (row) => setModal({ type: "svg", row });

  const sampleSubactions = [
    { action: "Check Budget", status: "Pending", owner: "Alice" },
    { action: "Review Design", status: "Done", owner: "Bob" },
    { action: "Approve Request", status: "In Progress", owner: "Charlie" },
  ];
  const [sortConfig, setSortConfig] = useState({ col: null, dir: null });

  const tableRef = useRef(null);
  const popupRef = useRef(null);
  const headerRef = useRef();  

// Sort helper that sorts the current `data` array in-place (keeps filtered rows/order)
const handleSort = (col, dir) => {
  setData(prev => {
    const copy = [...prev];
    copy.sort((a, b) => {
      const A = String(a[col] ?? "").toLowerCase();
      const B = String(b[col] ?? "").toLowerCase();
      return dir === "asc" ? A.localeCompare(B) : B.localeCompare(A);
    });
    return copy;
  });
};

// Toggle sort: click cycles asc -> desc -> asc (you can change to asc -> desc -> none if desired)
const handleSortToggle = (col) => {
  setSortConfig(prev => {
    const nextDir = prev.col !== col ? "asc" : (prev.dir === "asc" ? "desc" : "asc");
    // apply sorting immediately when toggled
    handleSort(col, nextDir);
    return { col, dir: nextDir };
  });
};
  const getUniqueValues = (col) => {
    const values = [...new Set(data.map(row => String(row[col])))];
    return values.sort();
  };
  // Filtered data
  const filteredData = data.filter(row =>
    columns.every(col => {
      const cellValue = row[col] ?? "";          // fallback to empty string if undefined/null
      const filterValue = filters[col] ?? "";    // fallback to empty string

      const textMatch = String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
      const checked = checkboxFilters[col];
      if (!checked || checked.length === 0) return textMatch;

      return checked.includes(String(cellValue));
    })
  );
const openFilterPopup = (col, e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const popupWidth = 220;
  const popupHeight = 260;
  const padding = 8;

  // Absolute coordinates
  let left = rect.left + window.scrollX;
  let top = rect.bottom + window.scrollY;

  const screenWidth = window.innerWidth + window.scrollX;
  const screenHeight = window.innerHeight + window.scrollY;

  // Adjust if right or bottom overflow
  if (left + popupWidth + padding > screenWidth) left = screenWidth - popupWidth - padding;
  if (top + popupHeight + padding > screenHeight) top = screenHeight - popupHeight - padding;

  setPopupPos({ top, left });
  setActiveFilterPopup(col);
  setPopupSearch("");
};
  const closePopup = () => setActiveFilterPopup(null);

  // Toggle row selection
  const toggleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  // Add row after last selected
  const handleAdd = () => {
    const lastSelectedIndex = Math.max(
      ...data.map((r, idx) => (selectedRows.has(r.ID) ? idx : -1)),
      -1
    );
    const newID = data.length ? Math.max(...data.map(d => d.ID)) + 1 : 1;
    const newRow = {
      ID: newID,
      Name: `Employee ${newID}`,
      Department: "New Dept",
      Country: "New Country",
      Salary: "0",
    };
    const newData = [...data];
    newData.splice(lastSelectedIndex + 1, 0, newRow);
    setData(newData);
  };
  const columnConstraints = {
    Department: ["Engineering", "Marketing", "HR", "Sales", "Finance"],
    Country: ["USA", "UK", "Germany", "India", "Japan"]
  };

  // Remove selected rows
  const handleRemove = () => {
    setData(prev => prev.filter(row => !selectedRows.has(row.ID)));
    setSelectedRows(new Set());
  };

  // Open edit modal for a single selected row
  const handleEdit = () => {
    if (selectedRows.size !== 1) {
      alert("Please select exactly one row to edit.");
      return;
    }
    const id = Array.from(selectedRows)[0];
    const rowToEdit = data.find(r => r.ID === id);
    
    // Open modal of type 'edit'
    setModal({ type: "edit", row: { ...rowToEdit } });
  };


  // Save edited row
  const saveEdit = () => {
    setData(prev => prev.map(r => (r.ID === editRow.ID ? editRow : r)));
    setEditRow(null);
    setSelectedRows(new Set());
  };
  const summarySettings = {
    Salary: "sum",   // show sum of Salary
    Col1: "sum",     // sum Col1
    Col2: "count",   // count non-empty Col2 cells
    Name: "count",   // count rows
    Department: "none", // no summary
  };
  const toNumber = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return val;

    // remove commas + trim text
    const cleaned = String(val).replace(/[^0-9.-]/g, "");
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
  };

const summary = useMemo(() => {
  const result = {};

  columns.forEach(col => {
    const action = summarySettings[col] || "none";

    if (action === "sum") {
      result[col] = filteredData
        .reduce((acc, row) => acc + toNumber(row[col]), 0)
        .toLocaleString();
    }

    else if (action === "count") {
      result[col] = filteredData.filter(row => row[col] != null && row[col] !== "").length;
    }

    else if (action === "average") {
      const valid = filteredData.map(r => toNumber(r[col])).filter(n => n > 0);
      const avg = valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
      result[col] = avg.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    else {
      result[col] = "";
    }
  });
  result.Name = "Summary";
  return result;
}, [filteredData]);

// Sync table data with API
useEffect(() => {
  setData(pageDataset?.data || []);
}, [pageDataset]);

  // const [ifcModal, setIfcModal] = useState({ open: false, fileUrl: null });
  useEffect(() => {
    console.log("PageDataset updated in Table:", pageDataset);
  }, [pageDataset]);  // This will log every time `pageDataset` changes
  useEffect(() => {
    if (activeFilterPopup && headerRefs.current[activeFilterPopup]) {
    const rect = headerRefs.current[activeFilterPopup].getBoundingClientRect();
    setPopupPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    }
  }, [activeFilterPopup]);


  useEffect(() => {
    if (editRow || modal) {
      // Freeze background scroll
      document.body.style.overflow = "hidden";
    } else {
      // Restore scroll
      document.body.style.overflow = "auto";
    }

    return () => {
      // Cleanup if component unmounts
      document.body.style.overflow = "auto";
    };
  }, [editRow, modal]);

  // if (!data || data.length === 0)
  //   return <div>No data</div>;



  return (

    <div
      ref={containerRef}
      style={{
        position: "relative",
        overflowX: "auto",
        overflowY: "auto",
        flex: 1
      }}
    >
      <table style={{ borderCollapse: "collapse", minWidth: "900px", tableLayout: "auto" }}>
        {/* Header */}
        <thead ref={headerRef}>
            <tr
              style={{
                background: "#107C41",   // Excel-green
                color: "white",   // text color
              }}
            >
            <th
              style={{
                width: "140px",
                borderBottom: "1px solid #ccc",
                borderRight: "1px solid #ddd",
                fontWeight: 600,
                textAlign: "center",

                /* FREEZE 1st COLUMN */
                position: "sticky",
                left: 0,
                top: 0,
                zIndex: 9,
                background: "#107C41",
                color: "white"
              }}
            >
              Actions
            </th>
            <th
              style={{
                borderBottom: "1px solid #ccc",
                borderRight: "1px solid #ddd",
                padding: "8px",
                fontWeight: 600,
                textAlign: "center",

                /* FREEZE 2nd COLUMN */
                position: "sticky",
                left: 140,     // same width as Actions column
                top: 0,
                zIndex: 5,
                background: "#107C41",
                color: "white"
              }}
            >
              #
            </th>

            {columns.map(col => (
              <th
                key={col}
                ref={el => (headerRefs.current[col] = el)}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 3,

                  borderBottom: "1px solid #ccc",
                  borderRight: "1px solid #ddd",
                  padding: "8px",
                  textAlign: "left",
                  userSelect: "none",

                  /* REQUIRED to make sticky header look frozen */
                  background: "#107C41",
                  color: "white"
                }}
              >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    {/* Column label (click to sort) */}
                    <div
                      onClick={() => handleSortToggle(col)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", flex: 1 }}
                      title={`Sort ${col} (click to toggle)`} 
                    >
                      <span style={{ fontWeight: 600 }}>{col}</span>

                      {/* Show sort indicator if this column is sorted */}
                      {sortConfig.col === col && (
                        <span style={{ fontSize: 12, opacity: 0.85, color: 'white' }}>
                          {sortConfig.dir === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>

                    {/* Small filter icon — keeps opening the popup */}
                    <span
                      style={{ cursor: "pointer", marginLeft: "6px", fontSize: "12px", padding: "2px 4px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openFilterPopup(col, e);
                      }}
                      title="Filter"
                    >
                      ▼
                    </span>
                  </div>
                </th>
              ))}

          </tr>

          {/* Filter Row with Dropdown */}
          <tr style={{ background: "#fafafa" }}>
            <th
              style={{
                textAlign: "center",
                padding: "4px",

                position: "sticky",
                top: 35,     // 🔥 IMPORTANT — below the header row
                left: 0,
                zIndex: 9,

                background: "#fafafa"
              }}
            >

              <select
                onChange={e => {
                  const action = e.target.value;
                  if (action === "add") handleAdd();
                  if (action === "edit") handleEdit();
                  if (action === "remove") handleRemove();
                  e.target.value = ""; // reset
                }}
                style={{ width: "100%", padding: "2px" }}
              >
                <option value="">Actions</option>
                <option value="add">Add</option>
                <option value="edit">Edit</option>
                <option value="remove">Remove</option>
              </select>
            </th>
              <th
                style={{
                  padding: "4px",
                  position: "sticky",
                  top: 35,
                  left: 140,
                  zIndex: 9,
                  background: "#fafafa"
                }}
              >

            </th>

            {columns.map(col => (
              <th key={`${col}-filter`} 
                  style={{
                    padding: "4px",
                    position: "sticky",
                    top: 36,   // 👈 required so filter row stays under main header
                    zIndex: 5,
                    background: "#fafafa",
                  }}                
                >
                {columnConstraints[col] ? (
                  // Dropdown if constrained
                  <select
                    value={filters[col]}
                    onChange={e => setFilters(prev => ({ ...prev, [col]: e.target.value }))}
                    style={{ width: "95%", padding: "2px 4px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.8rem" }}
                  >
                    <option value="">All</option>
                    {columnConstraints[col].map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                ) : (
                  // Default text input
                  <input
                    type="text"
                    value={filters[col]}
                    onChange={e => setFilters(prev => ({ ...prev, [col]: e.target.value }))}
                    placeholder={`Filter ${col}`}
                    style={{ width: "95%", padding: "2px 4px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "0.8rem" }}
                  />
                )}
              </th>
            ))}
            
          </tr>          
        </thead>

        {/* Table Body */}
        <tbody>
          {filteredData.map((row, idx) => (
            <tr
              key={row.ID}
              data-tenant={row.Country}
              data-ifcfile={row.IFCFile} 
              data-ifcurl={`/tenants/${row.Country}/data/${row.IFCFile}`}
              onClick={() => toggleSelectRow(row.ID)}
              style={{
                background: selectedRows.has(row.ID) ? "#d0ebff" : idx % 2 === 0 ? "#fff" : "#f9f9f9",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
            >
              {/* Actions Column */}
              <td
                style={{
                  width:"140px",
                  textAlign: "center",
                  borderBottom: "1px solid #eee",
                  borderRight: "1px solid #f0f0f0",
                  padding: "2px",
                  position: "sticky",
                  left: 0,
                  background: selectedRows.has(row.ID) ? "#d0ebff" : idx % 2 === 0 ? "#fff" : "#f9f9f9",
                  zIndex: 3
                }}
              >
                <div style={{ display: "flex", justifyContent: "center", gap: "1px" }}>
                  
                  {/* Always show EDIT */}
                  <button
                    title="Edit"
                    style={iconBtnStyle}
                    onClick={e => {
                      e.stopPropagation();
                      setModal({ type: "edit", row: row });
                    }}
                  >
                    ✏️
                  </button>

                  {/* If section === 'user' show full action set */}
                  {section === "user" ? (
                    <>
                      <button
                        title="Checklist"
                        style={iconBtnStyle}
                        onClick={e => {
                          e.stopPropagation();
                          setModal({ type: "checklist", row: row });
                        }}
                      >
                        ☑️
                      </button>

                      <button title="Issue" style={iconBtnStyle} onClick={e => e.stopPropagation()}>
                        ❗
                      </button>

                      <button title="Attachment" style={iconBtnStyle} onClick={e => e.stopPropagation()}>
                        📎
                      </button>

                      <button
                        title="Subsheet"
                        style={iconBtnStyle}
                        onClick={e => {
                          e.stopPropagation();
                          alert(`Subsheet action for row ID ${row.ID}`);
                        }}
                      >
                        📂
                      </button>

                      <button
                        style={iconBtnStyle}
                        onClick={e => {
                          e.stopPropagation();
                          setModal({ type: "svg", points: samplePoints });
                        }}
                      >
                        🏗️
                      </button>
                    </>
                  ) : null}

                  {/* Always show COMMENTS */}
                  <button
                    title="Comments"
                    style={iconBtnStyle}
                    onClick={e => e.stopPropagation()}
                  >
                    💬
                  </button>
                </div>
              </td>


              {/* Row number */}
              <td
                style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid #eee",
                  borderRight: "1px solid #f0f0f0",
                  textAlign: "center",

                  /* FREEZE 2nd COLUMN */
                  position: "sticky",
                  left: 140,
                  background: selectedRows.has(row.ID) ? "#d0ebff" : idx % 2 === 0 ? "#fff" : "#f9f9f9",
                  zIndex: 3
                }}
              >
                {idx + 1}
              </td>


              {/* Data Columns */}
              {columns.map(col => (
                <td key={col} style={{ padding: "6px 8px", borderBottom: "1px solid #eee", borderRight: "1px solid #f0f0f0", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                  {row[col]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr
            style={{
              background: "#f0f0f0",
              fontWeight: 600,
              borderTop: "2px solid #ccc",
            }}
          >
            {/* FIRST COLUMN — Sticky left + Sticky bottom */}
            <td
              style={{
                position: "sticky",
                left: 0,
                bottom: 0,
                background: "#f0f0f0",
                zIndex: 6,
                padding: "6px 8px",
              }}
            >
              —
            </td>

            {/* SECOND COLUMN — Sticky left + Sticky bottom */}
            <td
              style={{
                position: "sticky",
                left: 140,     // same offset as your header second cell
                bottom: 0,
                background: "#f0f0f0",
                zIndex: 6,
                padding: "6px 8px",
              }}
            >
              #
            </td>

            {/* OTHER COLUMNS — Sticky-bottom only */}
            {columns.map(col => (
              <td
                key={col}
                style={{
                  position: "sticky",
                  bottom: 0,
                  background: "#f0f0f0",
                  padding: "6px 8px",
                  borderTop: "1px solid #ccc",
                  zIndex: 5,
                }}
              >
                {summary[col]}
              </td>
            ))}
          </tr>
        </tfoot>

      </table>
      {activeFilterPopup &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: "absolute",
              top: popupPos.top,
              left: popupPos.left,
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 6,
              width: 220,
              maxHeight: 260,
              overflow: "auto",
              padding: 10,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
            }}
          >
            {/* Your popup content */}


                  <div style={{ margin: "8px", textAlign: "center" }}>
                    <button onClick={closePopup}>Close</button>
                    <button
                      style={{ marginLeft: 6 }}
                      onClick={() =>
                        setCheckboxFilters(prev => ({
                          ...prev,
                          [activeFilterPopup]: []
                        }))
                      }
                    >
                      Clear
                    </button>
                  </div>

                  <label>
                    <input
                      type="checkbox"
                      checked={
                        (checkboxFilters[activeFilterPopup] || []).length ===
                        getUniqueValues(activeFilterPopup).length
                      }
                      onChange={e => {
                        setCheckboxFilters(prev => ({
                          ...prev,
                          [activeFilterPopup]: e.target.checked
                            ? getUniqueValues(activeFilterPopup)
                            : []
                        }));
                      }}
                    />
                    Select All
                  </label>

                  {getUniqueValues(activeFilterPopup).map(v => (
                    <label key={v} style={{ display: "flex", gap: "6px" }}>
                      <input
                        type="checkbox"
                        checked={(checkboxFilters[activeFilterPopup] || []).includes(v)}
                        onChange={e => {
                          setCheckboxFilters(prev => {
                            const current = prev[activeFilterPopup] || [];
                            return {
                              ...prev,
                              [activeFilterPopup]: e.target.checked
                                ? [...current, v]
                                : current.filter(i => i !== v)
                            };
                          });
                        }}
                      />
                      {v}
                    </label>
                  ))}
                </div>,
                document.body
              )
            }


      {/* === Edit Modal === */}
      {modal.type && (
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 10, background: "rgba(0,0,0,0.3)",
        display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        <div style={{ background: "#fff", padding: "20px", borderRadius: "6px", minWidth: "300px", maxHeight: "80vh", overflowY: "auto" }}>
          {modal.type === "edit" && (
            <>
              <h3>Edit Row {modal.row.ID}</h3>
              {columns.filter(c => c !== "ID").map(col => (
                <div key={col} style={{ margin: "8px 0" }}>
                  <label style={{ display: "block", fontSize: "0.9rem" }}>{col}</label>
                  <input
                    type="text"
                    value={modal.row[col]}
                    onChange={e => setModal(prev => ({ ...prev, row: { ...prev.row, [col]: e.target.value } }))}
                    style={{ width: "100%", padding: "4px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                </div>
              ))}
            </>
          )}
          {modal?.type === "svg" && (
            
          //   <SvgModal
          //     points={points}
          //     setPoints={setPoints}
          //     onClose={() => setModal({ type: null, row: null })}
          // />
          <TractumSvgEditor
              points = {tractumPoints}
              onClose={() => setModal({ type: null, row: null })}
          />
          )}
          {modal.type === "checklist" && (
            <>
              <h3>Checklist for {modal.row.Name}</h3>

              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px" }}>
                <thead>
                  <tr style={{ background: "#f3f3f3" }}>
                    <th style={{ border: "1px solid #ccc", padding: "4px" }}>Task</th>
                    <th style={{ border: "1px solid #ccc", padding: "4px" }}>Done</th>
                    <th style={{ border: "1px solid #ccc", padding: "4px" }}>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {["Task 1", "Task 2", "Task 3"].map((task, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                      <td style={{ border: "1px solid #ccc", padding: "4px" }}>{task}</td>
                      <td style={{ border: "1px solid #ccc", padding: "4px", textAlign: "center" }}>
                        <input type="checkbox" />
                      </td>
                      <td style={{ border: "1px solid #ccc", padding: "4px" }}>Owner {idx + 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}


          <div style={{ marginTop: "12px", textAlign: "right" }}>
            <button onClick={() => setModal({ type: null, row: null })} style={{ marginRight: "8px" }}>Close</button>
            {modal.type === "edit" && <button onClick={saveEdit}>Save</button>}
          </div>
        </div>
      </div>
      

    )}

    </div>

  );
}

const iconBtnStyle = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: "14px",
  padding: "2px",
  margin: 0,
  transition: "transform 0.1s",
};